import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import TimerIcon from '@mui/icons-material/Timer';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { StepCard } from './StepCard';
import { STEP_TYPES, STEP_TYPE_LABELS } from '../types/steps';
import type {
  ScriptStep,
  ScriptStepWithId,
  StepType,
  TestPointRef
} from '../types/steps';

type Props = {
  steps: ScriptStepWithId[];
  testPointRef?: TestPointRef | null;
  sectionLabel?: string;
  readOnly?: boolean;
  allowedStepTypes?: StepType[];
  onAddStep: (type: StepType) => void;
  onRemoveStep: (id: string) => void;
  onUpdateStep: (id: string, updates: Partial<ScriptStep>) => void;
  onReorder: (activeId: string, overId: string) => void;
};

const STEP_MENU_ITEMS: Array<{ type: StepType; icon: React.ReactNode }> = [
  { type: STEP_TYPES.COMMAND, icon: <SendIcon fontSize="small" /> },
  {
    type: STEP_TYPES.GET_RESULT_AND_PIC,
    icon: <CameraAltIcon fontSize="small" />
  },
  { type: STEP_TYPES.WAIT, icon: <TimerIcon fontSize="small" /> }
];

export const StepList = ({
  steps,
  testPointRef,
  sectionLabel,
  readOnly,
  allowedStepTypes,
  onAddStep,
  onRemoveStep,
  onUpdateStep,
  onReorder
}: Props) => {
  const menuItems = allowedStepTypes
    ? STEP_MENU_ITEMS.filter(item => allowedStepTypes.includes(item.type))
    : STEP_MENU_ITEMS;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [newestStepId, setNewestStepId] = useState<string | null>(null);
  const prevStepIdsRef = useRef(new Set(steps.map(s => s.id)));

  useEffect(() => {
    const prevIds = prevStepIdsRef.current;
    const added = steps.find(s => !prevIds.has(s.id));
    if (added) {
      setNewestStepId(added.id);
    }
    prevStepIdsRef.current = new Set(steps.map(s => s.id));
  }, [steps]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onReorder(String(active.id), String(over.id));
    }
  };

  const handleAddClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSelectType = (type: StepType) => {
    onAddStep(type);
    handleMenuClose();
  };

  return (
    <Box sx={readOnly ? { opacity: 0.7, pointerEvents: 'none' } : undefined}>
      {sectionLabel && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2
          }}
        >
          <Typography variant="subtitle2">
            {sectionLabel} ({steps.length})
          </Typography>
        </Box>
      )}

      <DndContext
        sensors={readOnly ? [] : sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={steps.map(s => s.id)}
          strategy={verticalListSortingStrategy}
        >
          {steps.map(step => (
            <StepCard
              key={step.id}
              step={step}
              testPointRef={testPointRef}
              defaultExpanded={step.id === newestStepId}
              onUpdate={onUpdateStep}
              onDelete={onRemoveStep}
            />
          ))}
        </SortableContext>
      </DndContext>

      {!readOnly && steps.length === 0 && (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            border: 1,
            borderColor: 'divider',
            borderStyle: 'dashed',
            borderRadius: 1,
            mb: 2
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No steps yet. Add a step to get started.
          </Typography>
        </Box>
      )}

      {!readOnly && (
        <>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            fullWidth
          >
            Add Step
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {menuItems.map(item => (
              <MenuItem
                key={item.type}
                onClick={() => handleSelectType(item.type)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText>{STEP_TYPE_LABELS[item.type]}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Box>
  );
};
